// =====================================================================
// TARPI — Overlays (mount on EVERY page)
// Cart drawer + Product detail + Tweaks panel
// Subscribes to window.TARPI_store for cart state
// =====================================================================

const { useState, useEffect, useSyncExternalStore } = React;

function useCart() {
  const subscribe = (cb) => window.TARPI_store.subscribe(cb);
  const getSnapshot = () => window.TARPI_store.get();
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

function OverlaysApp() {
  const cart = useCart();
  const [cartOpen, setCartOpen] = useState(false);
  const [activeProduct, setActiveProduct] = useState(null);

  // Tweaks
  const TWEAK_DEFAULTS = /*EDITMODE-BEGIN*/{
    "accentPair": ["#1B9E8F", "#4AB965"],
    "displayFont": "Bricolage Grotesque",
    "motionIntensity": 1,
    "showGrain": true
  }/*EDITMODE-END*/;
  const [t, setTweak] = window.useTweaks ? window.useTweaks(TWEAK_DEFAULTS) : [TWEAK_DEFAULTS, () => {}];

  useEffect(() => {
    const r = document.documentElement;
    if (Array.isArray(t.accentPair)) {
      r.style.setProperty("--tarpi-teal", t.accentPair[0]);
      r.style.setProperty("--tarpi-green", t.accentPair[1]);
    }
    r.style.setProperty("--font-display", `"${t.displayFont}", system-ui, sans-serif`);
    r.style.setProperty("--motion-scale", t.motionIntensity);
    document.body.classList.toggle("no-grain", !t.showGrain);
  }, [t]);

  // Wire global events
  useEffect(() => {
    const onOpenCart = () => setCartOpen(true);
    const onOpenProduct = (e) => setActiveProduct(e.detail.product);
    const onLineFilter = (e) => {
      const catalogSection = document.getElementById("catalogo");
      if (catalogSection) {
        catalogSection.scrollIntoView({behavior: "smooth", block: "start"});
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent("tarpi:set-catalog-line", {detail: {line: e.detail.line}}));
        }, 700);
      } else {
        location.href = (window.tarpiData?.homeUrl || location.origin + '/') + '#catalogo';
      }
    };
    window.addEventListener("tarpi:open-cart", onOpenCart);
    window.addEventListener("tarpi:open-product", onOpenProduct);
    window.addEventListener("tarpi:filter-line", onLineFilter);
    return () => {
      window.removeEventListener("tarpi:open-cart", onOpenCart);
      window.removeEventListener("tarpi:open-product", onOpenProduct);
      window.removeEventListener("tarpi:filter-line", onLineFilter);
    };
  }, []);

  // Cart setter adapter for QuoteCart child
  const setCart = (next) => window.TARPI_store.set(next);

  return (
    <React.Fragment>
      <window.ProductDetail
        product={activeProduct}
        onClose={() => setActiveProduct(null)}
        onAdd={(p) => window.TARPI_store.add(p)}
        inCart={!!(activeProduct && cart.find(c => c.id === activeProduct.id))}
      />
      <window.QuoteCart
        open={cartOpen}
        onClose={() => setCartOpen(false)}
        cart={cart}
        setCart={setCart}
      />
      {window.TweaksPanel && (
        <window.TweaksPanel title="Tweaks · TARPI">
          <window.TweakSection label="Identidad">
            <window.TweakColor
              label="Gradiente de marca"
              value={t.accentPair}
              onChange={(v) => setTweak("accentPair", v)}
              options={[
                ["#1B9E8F", "#4AB965"],
                ["#0F4D3A", "#5DC97E"],
                ["#137168", "#2F8E47"],
                ["#1A6FB8", "#36C5F4"],
                ["#7C3AED", "#22D3EE"]
              ]}
            />
            <window.TweakSelect
              label="Tipografía display"
              value={t.displayFont}
              onChange={(v) => setTweak("displayFont", v)}
              options={["Bricolage Grotesque", "Schibsted Grotesk", "Instrument Serif", "Space Grotesk"]}
            />
          </window.TweakSection>
          <window.TweakSection label="Movimiento">
            <window.TweakSlider
              label="Intensidad parallax"
              value={t.motionIntensity}
              onChange={(v) => setTweak("motionIntensity", v)}
              min={0} max={2} step={0.1}
            />
            <window.TweakToggle
              label="Textura grain"
              value={t.showGrain}
              onChange={(v) => setTweak("showGrain", v)}
            />
          </window.TweakSection>
        </window.TweaksPanel>
      )}
    </React.Fragment>
  );
}

ReactDOM.createRoot(document.getElementById("overlays-root")).render(<OverlaysApp />);
